'use client';

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import Link from 'next/link';
import './spend.css';

/* All styling lives in spend.css (scoped to .spend-page).
   Images go in public/assets/images/ .
   "Industry Scenarios" cards: har card ka image path aur link neeche `industries` array me `img` / `href` field me badlo.
   Jis card ka page nahi hai (Auto & Industrial) uska href nahi hai, wo plain card rahega. */

type IconName = 'eye' | 'warn' | 'grid' | 'file' | 'coin' | 'chart' | 'shield' | 'q' | 'plus' | 'doc' | 'down';

function Icon({ name }: { name: IconName }) {
  const p: Record<IconName, ReactNode> = {
    eye: <><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></>,
    warn: <><path d="M12 4 3 20h18z" /><path d="M12 10v4M12 17h.01" /></>,
    grid: <><rect x="4" y="4" width="7" height="7" /><rect x="13" y="4" width="7" height="7" /><rect x="4" y="13" width="7" height="7" /><rect x="13" y="13" width="7" height="7" /></>,
    file: <><path d="M7 3h7l5 5v13H7z" /><path d="M14 3v5h5M10 13h6M10 17h6" /></>,
    coin: <><ellipse cx="12" cy="6" rx="7" ry="3" /><path d="M5 6v6c0 1.7 3 3 7 3s7-1.3 7-3V6M5 12v6c0 1.7 3 3 7 3s7-1.3 7-3v-6" /></>,
    chart: <><path d="M5 20V12M10 20V6M15 20v-9M20 20V9" /></>,
    shield: <><path d="M12 3 5 6v5c0 4.5 3 8 7 10 4-2 7-5.5 7-10V6z" /><path d="m9 12 2 2 4-4" /></>,
    q: <><circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 .9-1 1.7M12 17h.01" /></>,
    plus: <><circle cx="12" cy="12" r="9" /><path d="M12 8v8M8 12h8" /></>,
    doc: <><path d="M7 3h7l5 5v13H7z" /><path d="M14 3v5h5" /></>,
    down: <><path d="M12 4v12M7 11l5 5 5-5M5 20h14" /></>,
  };
  return (
    <svg className="sp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {p[name]}
    </svg>
  );
}

/* ============================== DATA ============================== */

const heroChips: { icon: IconName; label: string }[] = [
  { icon: 'eye', label: 'Spend Visibility' },
  { icon: 'warn', label: 'Supplier Risk' },
  { icon: 'grid', label: 'Category Optimisation' },
  { icon: 'file', label: 'Contract Compliance' },
];

const benefits: { icon: IconName; title: string; text: string; cls: string }[] = [
  { icon: 'coin', title: 'Lower Procurement Cost', text: 'Reduce maverick spend and achieve better pricing.', cls: 'tf-4 benefit--dark' },
  { icon: 'chart', title: 'Stronger Supplier Performance', text: 'Improve quality, delivery and compliance.', cls: 'tf-4' },
  { icon: 'eye', title: 'Higher Spend Visibility', text: 'Unify spend data across categories and business units.', cls: 'tf-4' },
  { icon: 'shield', title: 'Reduced Supply Risk', text: 'Identify and mitigate supplier, geo-political and operational risks.', cls: 'tf-8 benefit--photo' },
  { icon: 'grid', title: 'More Strategic Sourcing', text: 'Enable data-driven category strategies and long-term value.', cls: 'tf-12 benefit--wide' },
];

const questions = [
  'Where is our money going?',
  'Are we getting the best price?',
  'Which suppliers are high risk?',
  'How can we improve contract compliance?',
  'Where are the opportunities for savings?',
];

/* img = full path from public/ folder (extension ke saath). Har card ke liye alag file daalo.
   href = industry page ka link (header ke Industries menu se). */
const industries: { name: string; text: string; img: string; href?: string }[] = [
  {
    name: 'Retail & CPG',
    text: 'Optimise indirect spend and improve supplier performance.',
    img: '/assets/images/Retail-&-CPG1.jpg',
    href: '/industries/fmcg-retail',
  },
  {
    name: 'Manufacturing',
    text: 'Manage direct materials spend and supplier risk.',
    img: '/assets/images/manufacturing2.jpg',
    href: '/industries/manufacturing',
  },
  {
    name: 'Pharma & Healthcare',
    text: 'Ensure compliant and resilient supply chains.',
    img: '/assets/images/pharma.jpg',
    href: '/industries/pharma-lifescience',
  },
  {
    name: 'Auto & Industrial',
    text: 'Rationalise suppliers and reduce procurement complexity.',
    img: '/assets/images/Auto-&-Industrial1.jpg',
  },
  {
    name: 'Banking & Financial Services',
    text: 'Improve third-party risk and contract governance.',
    img: '/assets/images/bfsi.jpg',
    href: '/industries/bfsi',
  },
  {
    name: 'Energy & Utilities',
    text: 'Optimise category spend and manage supplier sustainability.',
    img: '/assets/images/Utilities-&-Energy.jpg',
    href: '/industries/energy-utilities',
  },
];

const useCases = [
  { title: 'Spend Analytics & Categorisation', text: 'Automatically classify and analyse spend across categories.' },
  { title: 'Supplier Risk Assessment', text: 'Identify and predict supplier risks using external and internal data.' },
  { title: 'Contract Intelligence', text: 'Extract key terms, monitor compliance and flag risks.' },
  { title: 'Savings Opportunity Identification', text: 'Detect cost-saving opportunities using AI/ML.' },
  { title: 'Supplier Performance Monitoring', text: 'Track on-time delivery, quality and service levels.' },
  { title: 'Conversational Procurement Assistant', text: 'Ask questions on spend, suppliers and contracts in natural language.' },
];

const problems = [
  'Lack of end-to-end spend visibility',
  'High maverick and off-contract spend',
  'Supplier performance issues',
  'Contract non-compliance',
  'Supply risk and single-source dependency',
];

const RESOURCES_HREF = '/insights/playbooks-downloads';

const resources = [
  { name: 'Procurement Maturity Assessment Template', type: 'Excel' },
  { name: 'Category Strategy Framework', type: 'PPT' },
  { name: 'Supplier Risk Assessment Checklist', type: 'PDF' },
  { name: 'Contract Compliance Dashboard Template', type: 'Excel' },
  { name: 'Spend Analytics Playbook', type: 'PPT' },
];

const journey = [
  { title: 'Digital Day', text: 'Align on business goals and use cases.' },
  { title: 'Discovery & Assessment', text: 'Assess data, processes and opportunities.' },
  { title: 'Solution Demo', text: 'Showcase tailored use cases with your data.' },
  { title: 'Pilot', text: 'Prove value in a focused scope (4–8 weeks).' },
  { title: 'Scale', text: 'Roll out across categories and business units.' },
];

/* ===================== SCROLL REVEAL ===================== */
/* Content stays visible if JS or IntersectionObserver is missing. */
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('rv--in');
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    root.querySelectorAll<HTMLElement>('.sec-head, .card, .uc-row, .res-row, .j-step').forEach((n, i) => {
      n.classList.add('rv');
      n.style.setProperty('--rv-delay', `${(i % 5) * 70}ms`);
      io.observe(n);
    });
    return () => io.disconnect();
  }, []);
  return ref;
}

/* ============================== PAGE ============================== */

export default function Spend() {
  const ref = useReveal();

  return (
    <div className="spend-page" ref={ref}>
      <main>
        {/* HERO */}
        <section className="sp-hero">
          <div className="sp-hero-bg"></div>
          <div className="sp-hero-overlay"></div>
          <div className="op-container sp-hero-inner">
            <div className="sp-hero-content">

              <h1 className="sp-hero-title">
                Spend &amp; Vendor
                <br />
                <span>Intelligence</span>
              </h1>
              <p className="sp-hero-lead">Smarter spend. Stronger suppliers. Greater value.</p>
              <p className="sp-hero-text">
                Use your data, business rules and AI to gain visibility into spend, optimise procurement,
                manage supplier risk and drive measurable savings across the source-to-pay cycle.
              </p>
              <div className="sp-actions">
                <Link className="btn-primary" href="/tellusyourproblem#tell-details">Book a Discovery Session</Link>
                <Link className="btn-outline" href="#use-cases">Explore Use Cases</Link>
              </div>
            </div>

          </div>
        </section>

        {/* TRANSFORM */}
        <section className="op-section op-section--white">
          <div className="op-container">
            <div className="tf-grid">
              <div className="tf-head">
                <div className="sec-head">
                  <div className="sec-badge">Spend &amp; Vendor Intelligence</div>
                  <h2 className="sec-title">Transform procurement into a strategic advantage</h2>
                  <p className="sec-sub sec-sub--base">
                    Move from reactive purchasing to a data-driven, intelligent source-to-pay process that
                    reduces cost, mitigates risk and builds resilient supplier ecosystems.
                  </p>
                </div>
              </div>
              {benefits.map((b, i) => (
                <div className={`card benefit ${b.cls}`} key={b.title}>
                  <div className="benefit-top">
                    <span className="benefit-num">{String(i + 1).padStart(2, '0')}</span>
                    <Icon name={b.icon} />
                  </div>
                  <div>
                    <h3 className="card-title">{b.title}</h3>
                    <p className="card-text">{b.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* KEY QUESTIONS */}
        <section className="op-section op-section--slate">
          <div className="op-container">
            <div className="sec-head">
              <div className="sec-badge">Real Decisions. Real Impact.</div>
              <h2 className="sec-title">Key Business Questions</h2>
              <p className="sec-sub sec-sub--base">Turn your spend and vendor questions into intelligent decisions.</p>
            </div>
            <div className="q-grid">
              {questions.map((q) => (
                <div className="card q-card" key={q}>
                  <span className="q-mark"><Icon name="q" /></span>
                  <p className="q-text">{q}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* INDUSTRIES */}
        <section className="op-section op-section--white">
          <div className="op-container">
            <div className="sec-head">
              <div className="sec-badge">Relevant Across Industries</div>
              <h2 className="sec-title">Industry Scenarios</h2>
              <p className="sec-sub sec-sub--base">Real-world scenarios to solve common spend and vendor challenges.</p>
            </div>
            <div className="ind-grid">
              {industries.map((ind) => {
                const body = (
                  <>
                    <div className="ind-img" style={{ background: '#f4f8fd' }}>
                      <img src={ind.img} alt={ind.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block', maxWidth: 'none' }} />
                    </div>
                    <div className="ind-body">
                      <h3 className="card-title">{ind.name}</h3>
                      <p className="card-text">{ind.text}</p>
                    </div>
                  </>
                );
                /* href na ho to card plain article rahega (click nahi hoga) */
                return ind.href ? (
                  <Link className="card ind-card" key={ind.name} href={ind.href}>
                    {body}
                  </Link>
                ) : (
                  <article className="card ind-card" key={ind.name}>
                    {body}
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* AI USE CASES */}
        <section className="op-section op-section--slate" id="use-cases">
          <div className="op-container">
            <div className="sec-head">
              <div className="sec-badge">From Insight to Action</div>
              <h2 className="sec-title">AI-Enabled Use Cases</h2>
              <p className="sec-sub sec-sub--base">Turn procurement data into actionable intelligence with AI.</p>
            </div>
            <div className="uc-grid">
              <div className="uc-list">
                {useCases.map((u, i) => (
                  <div className="uc-row" key={u.title}>
                    <span className="uc-num">{String(i + 1).padStart(2, '0')}</span>
                    <div className="uc-body">
                      <h3 className="uc-title">{u.title}</h3>
                      <p className="card-text">{u.text}</p>
                    </div>
                    <Link className="uc-link" href="/tellusyourproblem#tell-details">Use Case</Link>
                  </div>
                ))}
              </div>
              <div className="uc-photo" role="img" aria-label="Procurement analytics dashboard with spend and supplier maps"></div>
            </div>
          </div>
        </section>

        {/* PROBLEMS + RESOURCES */}
        <section className="op-section op-section--white" id="problems">
          <div className="op-container">
            <div className="split-grid">
              <div>
                <div className="sec-head">
                  <h2 className="sec-title">52 Spend &amp; Vendor Problems</h2>
                  <p className="sec-sub sec-sub--base">Real problems. Practical solutions.</p>
                </div>
                <div className="panel">
                  {problems.map((p) => (
                    <details className="acc" key={p}>
                      <summary><Icon name="plus" />{p}</summary>
                      <p className="card-text">Explore how Orane approaches this problem with your data and business rules.</p>
                    </details>
                  ))}
                </div>
              </div>
              <div>
                <div className="sec-head">
                  <h2 className="sec-title">Resources &amp; Downloads</h2>
                  <p className="sec-sub sec-sub--base">Templates, frameworks and case examples.</p>
                </div>
                <div className="panel">
                  {resources.map((r) => (
                    <Link className="res-row" href={RESOURCES_HREF} key={r.name}>
                      <span className={`res-icon res-icon--${r.type.toLowerCase()}`}><Icon name="doc" /></span>
                      <span className="res-body"><strong>{r.name}</strong><small>{r.type}</small></span>
                      <span className="res-dl" aria-label={`Download ${r.name}`}><Icon name="down" /></span>
                    </Link>
                  ))}
                </div>
                <div className="panel-foot">
                  <Link className="btn-primary" href={RESOURCES_HREF}>View All Spend &amp; Vendor Resources</Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ENGAGEMENT JOURNEY */}
        <section className="op-section op-section--slate">
          <div className="op-container">
            <div className="sec-head">
              <div className="sec-badge">The Journey to Impact</div>
              <h2 className="sec-title">Our Engagement Journey</h2>
              <p className="sec-sub sec-sub--base">From understanding to measurable outcomes.</p>
            </div>
            <ol className="journey">
              {journey.map((j, i) => (
                <li className="j-step" key={j.title}>
                  <span className="j-num">{i + 1}</span>
                  <h3 className="card-title">{j.title}</h3>
                  <p className="card-text">{j.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="sp-cta" id="contact">
          <div className="sp-cta-bg"></div>
          <div className="sp-cta-overlay"></div>
          <div className="op-container sp-cta-inner">
            <div>
              <p className="sp-cta-eyebrow">Stronger suppliers. Smarter spend. A more resilient business.</p>
              <h2 className="sp-cta-title">Do More. Together.</h2>
              <p className="sp-cta-text">
                Let’s explore how Spend &amp; Vendor Intelligence can create <strong>measurable value for your business.</strong>
              </p>
            </div>
            <Link className="btn-primary" href="/tellusyourproblem#tell-details">Talk to Our Team</Link>
          </div>
        </section>
      </main>
    </div>
  );
}